import { ImageAttachmentPicker, MessageImages, useImageAttachments } from "@/components/MessageImages";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useLocation } from "wouter";
import { MessageCircle, Send, ArrowLeft, Package, Briefcase, User, Wrench, CheckCircle } from "lucide-react";
import { WebAvatar } from "@/components/WebAvatar";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

interface ConvItem {
  id: number;
  freeItemId?: number | null;
  jobId?: number | null;
  posterId: number;
  inquirerId: number;
  lastMessageAt: string;
  item?: { id: number; title: string; imageUrl?: string | null; town?: string; type?: string } | null;
  otherUser?: { id: number; name: string; avatarUrl?: string | null } | null;
  lastMessage?: { text: string; senderId: number } | null;
  unreadCount: number;
}

interface Msg {
  attachmentIds?: string[];
  id: number;
  conversationId: number;
  senderId: number;
  text: string;
  isRead: boolean;
  createdAt: string;
  senderName?: string | null;
  senderAvatarUrl?: string | null;
  senderRole?: string | null;
}

interface ConvMeta {
  id: number;
  jobId?: number | null;
  freeItemId?: number | null;
  posterId: number;
  inquirerId: number;
  otherUser?: { id: number; name: string; avatarUrl?: string | null; role?: string } | null;
  tradeProfile?: { id: number; businessName?: string | null; categories?: string[] | null } | null;
  item?: { id: number; title: string; town?: string; type: "job" | "free_item" } | null;
}

function formatTimeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function formatMsgTime(iso: string) {
  return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

function formatMsgDate(iso: string) {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
}

export default function Messages() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const [activeConvId, setActiveConvId] = useState<number | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const freeItemId = params.get("freeItemId");
    const conversationId = params.get("conversationId") ?? params.get("conversation");
    const jobId = params.get("jobId");
    const userId = params.get("userId");

    if (conversationId) {
      const id = Number(conversationId);
      if (!isNaN(id) && id > 0) setActiveConvId(id);
    } else if (freeItemId && user) {
      fetch(`/api/free-items/${freeItemId}/conversations`, { method: "POST", headers: authHeaders() })
        .then(async (r) => {
          const body = await r.json() as { id?: number; error?: string };
          if (!r.ok) {
            toast({ title: "Can't start conversation", description: body.error ?? "Please contact the owner directly.", variant: "destructive" });
            return;
          }
          if (body?.id) setActiveConvId(body.id);
        })
        .catch(() => toast({ title: "Connection error", description: "Please try again.", variant: "destructive" }));
    } else if (jobId && user) {
      fetch(`/api/jobs/${jobId}/conversations`, { method: "POST", headers: authHeaders() })
        .then(async (r) => {
          const body = await r.json() as { id?: number; error?: string };
          if (!r.ok) {
            toast({ title: "Can't message resident", description: body.error ?? "You must unlock this lead first.", variant: "destructive" });
            return;
          }
          if (body?.id) setActiveConvId(body.id);
        })
        .catch(() => toast({ title: "Connection error", description: "Please try again.", variant: "destructive" }));
    } else if (userId && user) {
      fetch("/api/conversations/direct", { method: "POST", headers: authHeaders(), body: JSON.stringify({ userId: Number(userId) }) })
        .then(async (r) => {
          const body = await r.json() as { id?: number; error?: string };
          if (!r.ok) {
            toast({ title: "Can't start conversation", description: body.error ?? "Unknown error.", variant: "destructive" });
            return;
          }
          if (body?.id) setActiveConvId(body.id);
        })
        .catch(() => toast({ title: "Connection error", description: "Please try again.", variant: "destructive" }));
    }
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="text-center space-y-3">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
            <MessageCircle size={28} className="text-primary" />
          </div>
          <h2 className="font-bold text-slate-800 text-lg">Sign in to view messages</h2>
          <button onClick={() => setLocation("/login")} className="bg-primary text-white font-bold px-5 py-2.5 rounded-xl text-sm">Sign In</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-primary px-4 pt-6 pb-4 flex items-center gap-3">
        <button onClick={() => setLocation("/")} className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center text-white">
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-white font-bold text-xl">Messages</h1>
          <p className="text-white/70 text-sm">Your conversations</p>
        </div>
      </div>

      <div className="flex h-[calc(100vh-116px)] w-full">
        <ConversationList
          userId={user.id}
          activeConvId={activeConvId}
          onSelect={(id) => setActiveConvId(id)}
        />

        {activeConvId !== null ? (
          <ThreadView key={activeConvId}
            convId={activeConvId}
            userId={user.id}
            onBack={() => setActiveConvId(null)}
          />
        ) : (
          <div className="hidden md:flex flex-1 items-center justify-center bg-white border-l border-slate-100">
            <div className="text-center space-y-2">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
                <MessageCircle size={28} className="text-primary" />
              </div>
              <p className="font-semibold text-slate-700">Select a conversation</p>
              <p className="text-slate-400 text-sm">Choose a message thread from the left</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ConversationList({ userId, activeConvId, onSelect }: {
  userId: number; activeConvId: number | null; onSelect: (id: number) => void;
}) {
  const { data, isLoading } = useQuery<{ items: ConvItem[] }>({
    queryKey: ["my-conversations"],
    queryFn: () => fetch("/api/my-conversations", { headers: authHeaders() }).then((r) => r.json()) as Promise<{ items: ConvItem[] }>,
    refetchInterval: 10000,
  });

  const conversations = data?.items ?? [];

  return (
    <div className={`${activeConvId !== null ? "hidden md:flex" : "flex"} flex-col w-full md:w-80 bg-white border-r border-slate-100 overflow-y-auto`}>
      <div className="p-3 border-b border-slate-100">
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
          {conversations.length} conversation{conversations.length !== 1 ? "s" : ""}
        </p>
      </div>

      {isLoading ? (
        <div className="p-4 space-y-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />)}
        </div>
      ) : conversations.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center gap-3">
          <MessageCircle size={32} className="text-slate-300" />
          <p className="font-semibold text-slate-500 text-sm">No conversations yet</p>
          <p className="text-slate-400 text-xs">Messages from tradespeople and neighbours will appear here</p>
        </div>
      ) : (
        conversations.map((c) => {
          const isActive = activeConvId === c.id;
          const isMe = c.lastMessage?.senderId === userId;
          const isJob = !!c.jobId;
          const isSystem = c.posterId === 0 || c.otherUser?.id === 0;
          const primaryLabel = isSystem ? "Ayrshire Hub" : (c.otherUser?.name ?? c.item?.title ?? (isJob ? "Quote Request" : "Free Item"));
          const secondaryLabel = isSystem
            ? "System notification"
            : isJob
            ? `Re: ${c.item?.title ?? "Quote Request"}`
            : (c.otherUser?.name ?? c.item?.town ?? "");

          return (
            <button
              key={c.id}
              onClick={() => onSelect(c.id)}
              className={`w-full text-left p-4 border-b border-slate-50 hover:bg-slate-50 transition-colors ${isActive ? "bg-primary/5 border-l-2 border-l-primary" : ""}`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isSystem ? "bg-primary/10" : isJob ? "bg-primary/10" : "bg-emerald-100"}`}>
                  {isSystem
                    ? <User size={16} className="text-primary" />
                    : c.otherUser?.name
                    ? <span className="text-sm font-bold text-primary">{c.otherUser.name.charAt(0).toUpperCase()}</span>
                    : isJob
                      ? <Briefcase size={16} className="text-primary" />
                      : <Package size={16} className="text-emerald-600" />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-slate-800 text-sm truncate">{primaryLabel}</span>
                    <span className="text-xs text-slate-400 flex-shrink-0 ml-2">{formatTimeAgo(c.lastMessageAt)}</span>
                  </div>
                  <p className="text-xs text-slate-400 truncate mt-0.5">{secondaryLabel}</p>
                  {c.lastMessage && (
                    <p className="text-xs text-slate-500 truncate mt-0.5">
                      {isMe ? "You: " : ""}{c.lastMessage.text}
                    </p>
                  )}
                </div>
                {c.unreadCount > 0 && (
                  <span className="w-5 h-5 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                    {c.unreadCount}
                  </span>
                )}
              </div>
            </button>
          );
        })
      )}
    </div>
  );
}

function ThreadView({ convId, userId, onBack }: { convId: number; userId: number; onBack: () => void }) {
  const attachments = useImageAttachments(convId);
  const [sendError,setSendError] = useState("");
  const [text, setText] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);
  const initialScrollDone = useRef(false);
  const qc = useQueryClient();

  const { data: convMeta } = useQuery<ConvMeta>({
    queryKey: ["conv-meta", convId],
    queryFn: () => fetch(`/api/conversations/${convId}`, { headers: authHeaders() }).then((r) => r.json()) as Promise<ConvMeta>,
    staleTime: 30000,
    enabled: !!convId,
  });

  const { data: convListData } = useQuery<{ items: ConvItem[] }>({
    queryKey: ["my-conversations"],
    queryFn: () => fetch("/api/my-conversations", { headers: authHeaders() }).then((r) => r.json()) as Promise<{ items: ConvItem[] }>,
  });
  const convListItem = convListData?.items.find((c) => c.id === convId);

  const { data, isLoading } = useQuery<{ items: Msg[] }>({
    queryKey: ["messages", convId],
    queryFn: () => fetch(`/api/conversations/${convId}/messages`, { headers: authHeaders() }).then((r) => r.json()) as Promise<{ items: Msg[] }>,
    refetchInterval: 5000,
    enabled: !!convId,
  });

  const { mutateAsync: sendMsg, isPending } = useMutation({
    mutationFn: async (t: string) => {
      const r = await fetch(`/api/conversations/${convId}/messages`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ text: t, attachments: attachments.claims }),
      });
      if (!r.ok) throw new Error("Failed to send");
      return r.json() as Promise<Msg>;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["messages", convId] });
      void qc.invalidateQueries({ queryKey: ["my-conversations"] });
    },
  });

  const messages = data?.items ?? [];

  useEffect(() => {
    if (messages.length === 0) return;
    if (!initialScrollDone.current) {
      bottomRef.current?.scrollIntoView({ behavior: "instant" });
      initialScrollDone.current = true;
    } else {
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    }
  }, [messages.length]);

  const handleSend = async () => {
    const t = text.trim();
    if ((!t && !attachments.images.length) || isPending || attachments.uploading) return;
    setSendError("");
    try { await sendMsg(t); setText(""); attachments.clear(); }
    catch { setSendError("Message could not be sent. Your draft and images are still here; please try again."); }
  };

  const isJob = convMeta?.jobId != null || convListItem?.jobId != null;
  const otherUser = convMeta?.otherUser ?? convListItem?.otherUser;
  const tradeProfile = convMeta?.tradeProfile;
  const jobTitle = convMeta?.item?.title ?? convListItem?.item?.title ?? "";
  const freeItemTitle = !isJob ? (convMeta?.item?.title ?? convListItem?.item?.title ?? "") : "";
  const isSystem = otherUser?.id === 0 || convMeta?.posterId === 0 || convListItem?.posterId === 0;

  const headerLabel = isSystem ? "Ayrshire Hub" : (otherUser?.name ?? (isJob ? "Quote Request" : "Free Item Enquiry"));
  const headerSub = isSystem
    ? "System notification"
    : isJob
    ? `Re: Quote Request${jobTitle ? ` — ${jobTitle}` : ""}`
    : freeItemTitle ? `Re: ${freeItemTitle}` : "Free item enquiry";

  // Group messages by date
  const grouped: { dateLabel: string; msgs: Msg[] }[] = [];
  for (const m of messages) {
    const label = formatMsgDate(m.createdAt);
    const last = grouped[grouped.length - 1];
    if (last && last.dateLabel === label) {
      last.msgs.push(m);
    } else {
      grouped.push({ dateLabel: label, msgs: [m] });
    }
  }

  return (
    <div className="flex-1 flex flex-col bg-white min-w-0">
      {/* Thread header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
        <button onClick={onBack} className="md:hidden text-slate-400 hover:text-slate-600">
          <ArrowLeft size={20} />
        </button>
        <WebAvatar name={otherUser?.name ?? "?"} avatarUrl={otherUser?.avatarUrl} size={36} />
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-800 text-sm truncate">{headerLabel}</p>
          <p className="text-xs text-slate-400 truncate">{headerSub}</p>
        </div>
        {isJob && tradeProfile && (
          <div className="flex items-center gap-1.5 bg-primary/8 border border-primary/20 rounded-full px-2.5 py-1 flex-shrink-0">
            <CheckCircle size={11} className="text-primary" />
            <span className="text-[11px] font-semibold text-primary">Verified Tradesperson</span>
          </div>
        )}
      </div>

      {/* Job context banner — shown so resident knows which request this replies to */}
      {isJob && jobTitle && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-primary/5 border-b border-primary/10">
          <Briefcase size={13} className="text-primary flex-shrink-0" />
          <p className="text-xs text-primary">
            Quote request: <span className="font-semibold">{jobTitle}</span>
          </p>
        </div>
      )}

      {/* Free item context banner — shown so both parties know which item this conversation is about */}
      {!isJob && freeItemTitle && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-emerald-50 border-b border-emerald-100">
          <Package size={13} className="text-emerald-600 flex-shrink-0" />
          <p className="text-xs text-emerald-700">
            About: <span className="font-semibold">{freeItemTitle}</span>
          </p>
        </div>
      )}

      {/* Tradesperson identity panel — shown when in a trade conversation */}
      {isJob && tradeProfile && otherUser && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-slate-50 border-b border-slate-100">
          <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
            <Wrench size={13} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-slate-700 truncate">{otherUser.name}</p>
            {tradeProfile.categories?.length && (
              <p className="text-[11px] text-slate-400 truncate">{tradeProfile.categories[0]}</p>
            )}
          </div>
          <div className="flex items-center gap-1 text-[10px] text-emerald-600 font-semibold bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
            <CheckCircle size={10} />
            Verified
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4 space-y-1">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-2">
            <MessageCircle size={32} className="text-slate-300" />
            <p className="text-slate-400 text-sm">
              {isJob ? "Introduce yourself and explain how you can help." : "No messages yet. Say hello!"}
            </p>
          </div>
        ) : (
          grouped.map((group) => (
            <div key={group.dateLabel}>
              <div className="flex items-center gap-2 my-4">
                <div className="flex-1 h-px bg-slate-100" />
                <span className="text-[11px] text-slate-400 font-medium px-2">{group.dateLabel}</span>
                <div className="flex-1 h-px bg-slate-100" />
              </div>
              <div className="space-y-2">
                {group.msgs.map((m) => {
                  const isMe = m.senderId === userId;
                  const isSystemMsg = m.senderId === 0;
                  const isTrade = !isMe && !isSystemMsg && (m.senderRole === "tradesperson" || m.senderRole === "business");

                  if (isSystemMsg) {
                    return (
                      <div key={m.id} className="flex justify-center my-2">
                        <div className="flex flex-col items-center max-w-[80%] gap-1">
                          <div className="flex items-center gap-1.5 bg-primary/8 border border-primary/15 rounded-2xl px-4 py-2.5">
                            <CheckCircle size={13} className="text-primary flex-shrink-0" />
                            <p className="text-sm text-primary leading-relaxed">{m.text}</p>
                          </div>
                          <p className="text-[10px] text-slate-400">{formatMsgTime(m.createdAt)}</p>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={m.id} className={`flex items-end gap-2 ${isMe ? "justify-end" : "justify-start"}`}>
                      {!isMe && (
                        m.senderAvatarUrl
                          ? <img src={m.senderAvatarUrl} alt={m.senderName ?? ""} className="w-7 h-7 rounded-full object-cover flex-shrink-0 mb-0.5" />
                          : <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mb-0.5">
                              <span className="text-[11px] font-bold text-primary">{(m.senderName ?? "?").charAt(0).toUpperCase()}</span>
                            </div>
                      )}
                      <div className={`max-w-[72%] ${isMe ? "items-end" : "items-start"} flex flex-col gap-0.5`}>
                        {!isMe && (
                          <div className="flex items-center gap-1.5 ml-1">
                            {m.senderName && <span className="text-[11px] text-slate-400 font-medium">{m.senderName}</span>}
                            {isTrade && (
                              <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-primary bg-primary/10 border border-primary/20 px-1.5 py-0.5 rounded-full">
                                <Wrench size={8} />
                                Tradesperson
                              </span>
                            )}
                          </div>
                        )}
                        <div className={`rounded-2xl px-4 py-2.5 ${isMe ? "bg-primary text-white rounded-br-md" : "bg-slate-100 text-slate-800 rounded-bl-md"}`}>
                          <MessageImages ids={m.attachmentIds}/><p className="text-sm leading-relaxed whitespace-pre-wrap">{m.text}</p>
                        </div>
                        <p className={`text-[10px] px-1 ${isMe ? "text-slate-400 text-right" : "text-slate-400"}`}>
                          {formatMsgTime(m.createdAt)}{isMe && <> · {m.isRead ? "Read" : "Sent"}</>}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {isSystem ? (
        <div className="flex items-center justify-center gap-2 px-4 py-3 border-t border-slate-100 bg-slate-50">
          <CheckCircle size={13} className="text-primary" />
          <p className="text-xs text-slate-500">This is a notification from Ayrshire Hub</p>
        </div>
      ) : (
        <div className="border-t border-slate-100 p-3 space-y-2">
          <ImageAttachmentPicker state={attachments} disabled={isPending}/>
          {sendError&&<p role="alert" className="text-sm text-red-600">{sendError}</p>}
          <div className="flex items-end gap-2">
          <textarea
            value={text}
            disabled={isPending}
            onPaste={e=>{const files=Array.from(e.clipboardData.files).filter(f=>f.type.startsWith("image/"));if(files.length){e.preventDefault();void attachments.addFiles(files);}}}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void handleSend(); } }}
            placeholder="Type a message…"
            rows={1}
            className="flex-1 border border-slate-200 rounded-2xl px-4 py-2.5 text-sm resize-none outline-none focus:border-primary transition-colors max-h-24"
          />
          <button
            aria-label="Send message"
            onClick={() => void handleSend()}
            disabled={(!text.trim() && !attachments.images.length) || isPending || attachments.uploading}
            className="w-10 h-10 rounded-full bg-primary disabled:opacity-40 flex items-center justify-center hover:bg-primary/90 transition-colors flex-shrink-0"
          >
            {isPending
              ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              : <Send size={15} className="text-white" />
            }
          </button>
          </div>
        </div>
      )}
    </div>
  );
}
